/**
 * 마케팅 동의 셀프 철회 링크 — 토큰이 유효한 상태 화면 하단의 공용 조각(admin#2472).
 *
 * ## 왜 화면별 컴포넌트가 아니라 공용 조각인가
 * 철회 대상은 특정 화면이 아니다 — 대기중/호출됨/착석/픽업 준비 등 토큰이 살아 있는
 * 모든 화면에서 동일하게 제공되어야 한다. 화면마다 붙이면 표시 조건이 6곳으로
 * 흩어지므로, 조각만 여기 두고 노출 판정·호출은 페이지가
 * {@link useMarketingConsentWithdrawal} 하나로 소유한다.
 *
 * ## 철회 범위는 연락처 전역(admin#2491 동의 v2)
 * 서버는 이 번호(vault)의 미철회 동의 증거를 전부 철회하고 전역 억제를 세운다 —
 * 이 매장·이 등록만이 아니라 OLULO 와 참여 매장 전체의 마케팅 수신이 끊긴다.
 * 확인·완료 문구({@link WaitlistStrings.mcWithdrawDesc}/`mcWithdrawDone`)도 그 범위로
 * 말해야 한다. 여기서 화면 문구를 바꿀 때 범위 표현을 좁히지 말 것.
 *
 * ## 확인 UX
 * 줄 이탈(WaitingScreen `salirFila`)과 동일하게 AlertDialog 1회 확인을 거친다 —
 * 되돌릴 수 없는 동작에 같은 패턴을 쓴다.
 *
 * ## 결과는 3갈래다 (#884)
 * 완료(`withdrawn`) · 재시도 가능한 실패(`failed`) · 서버가 처리할 수 없었음
 * (`unavailable`, 409 `CONSENT_WITHDRAWAL_UNAVAILABLE`). 마지막 갈래를 완료로
 * 뭉개면 수신이 계속되는데도 끊겼다고 안내하게 되므로 절대 합치지 말 것.
 */
import { Fragment, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { WaitlistStrings } from './strings';

/**
 * 안내 문구 속 이메일 주소 — 15 locale 문장을 세그먼트로 쪼개지 않고 여기서 찾는다.
 *
 * 문구는 번역본마다 주소의 위치가 다르고(앞·뒤 조사/전치사), 문자열을 조각으로
 * 나누면 번역가가 조립 순서를 바꿀 방법이 사라진다. 대신 완성된 문장에서 주소만
 * 골라 링크로 감싼다 — 문구 계약(strings.test 의 리터럴 검사)은 그대로 유지된다.
 */
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;

/** 문장을 텍스트/이메일 조각으로 쪼갠다. */
function splitEmailSegments(text: string): Array<{ text: string; email?: string }> {
  const parts: Array<{ text: string; email?: string }> = [];
  let cursor = 0;

  for (const match of text.matchAll(EMAIL_PATTERN)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      parts.push({ text: text.slice(cursor, start) });
    }
    parts.push({ text: match[0], email: match[0] });
    cursor = start + match[0].length;
  }

  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor) });
  }

  return parts;
}

/**
 * 409 전용 안내 — 이메일 주소를 `mailto:` 로 링크화해 렌더한다.
 *
 * 이 문구의 존재 이유는 "막다른 길을 두지 않는 것"이다. 그런데 iOS Safari 는 임의
 * 텍스트의 이메일 주소를 (전화번호와 달리) 자동 링크화하지 않는다 — 모바일 웹 전용
 * 화면에서 고객이 주소를 손으로 옮겨 적어야 하고, 한 글자만 틀려도 개인정보 통제권
 * 행사 요청이 아무 데도 도달하지 않는다. 탭 한 번으로 메일 앱이 열려야 한다.
 */
function WaitlistMarketingConsentWithdrawUnavailable({ text }: { text: string }) {
  return (
    <p
      role="alert"
      data-testid="waitlist-marketing-consent-withdraw-unavailable"
      className="m-0 mb-1.5 text-xs leading-snug text-destructive-dark"
    >
      {splitEmailSegments(text).map((part, index) =>
        part.email ? (
          <a
            key={index}
            href={`mailto:${part.email}`}
            className="underline underline-offset-2"
          >
            {part.text}
          </a>
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </p>
  );
}

interface WaitlistMarketingConsentWithdrawProps {
  t: WaitlistStrings;
  /** 철회 요청 진행 중 — 중복 제출을 막는다. */
  withdrawing: boolean;
  /**
   * 철회 완료 — 서버 200, 또는 서버가 "닫을 동의 증거가 없다"고 답한 경우
   * (`CONSENT_NOT_FOUND`). 링크 대신 완료 문구를 띄운다. 맨 404 처럼 **철회가
   * 일어났는지 알 수 없는** 응답은 여기 들어오지 않는다(일반 실패로 간다).
   */
  withdrawn: boolean;
  /** 직전 시도 실패 — 링크는 그대로 두고 재시도할 수 있게 안내만 덧붙인다. */
  failed: boolean;
  /**
   * 서버가 철회를 처리할 수 없었음(409 `CONSENT_WITHDRAWAL_UNAVAILABLE`, admin#2493).
   *
   * 일반 실패와 달리 재시도해도 같은 결과가 나올 수 있으므로 "다시 시도" 대신 사람이
   * 처리할 수 있는 다른 경로를 안내한다. 링크(재시도 경로)는 그대로 둔다 — 일시적
   * 불일치였다면 다음 시도에서 풀리기 때문이다.
   */
  unavailable?: boolean;
  onWithdraw: () => void;
}

export function WaitlistMarketingConsentWithdraw({
  t,
  withdrawing,
  withdrawn,
  failed,
  unavailable = false,
  onWithdraw,
}: WaitlistMarketingConsentWithdrawProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (withdrawn) {
    return (
      <div
        data-testid="waitlist-marketing-consent-withdrawn"
        role="status"
        aria-live="polite"
        className="shrink-0 px-5 pb-5 pt-1 text-center text-xs leading-snug text-muted-foreground"
      >
        {t.mcWithdrawDone}
      </div>
    );
  }

  return (
    <>
      <div className="shrink-0 px-5 pb-5 pt-1 text-center">
        {/*
          409 전용 안내가 일반 오류보다 우선한다 — 둘이 동시에 서면 "다시 시도"와
          "다른 경로로 요청"이 겹쳐 고객이 무엇을 해야 할지 흐려진다.
        */}
        {unavailable ? (
          <WaitlistMarketingConsentWithdrawUnavailable text={t.mcWithdrawUnavailable} />
        ) : (
          failed && (
            <p
              role="alert"
              className="m-0 mb-1.5 text-xs leading-snug text-destructive-dark"
            >
              {t.mcWithdrawError}
            </p>
          )
        )}
        <button
          type="button"
          data-testid="waitlist-marketing-consent-withdraw"
          disabled={withdrawing}
          onClick={() => setDialogOpen(true)}
          className="bg-transparent p-0 text-xs text-muted-foreground underline underline-offset-2 disabled:opacity-60"
        >
          {t.mcWithdrawLink}
        </button>
      </div>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.mcWithdrawTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t.mcWithdrawDesc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDialogOpen(false)}>{t.cancelar}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDialogOpen(false);
                onWithdraw();
              }}
            >
              {t.mcWithdrawCta}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
